import { useEffect } from 'react';
import './GunkShotAnimation.css';

/**
 * PROFESSIONAL GUNK SHOT ANIMATION
 * Poison-type physical move - hurls filthy sludge at the opponent
 * 
 * Key Features:
 * - Poison-type physical attack (120 base power, 80% accuracy)
 * - 30% chance to poison
 * - Toxic sludge projectile with splatter impact
 * - Professional gaming quality with viscous toxic effects
 */
export default function GunkShotAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Toxic Charge (0-400ms)
    playerEl.classList.add('gunk-shot-user-charge');

    const toxicAura = document.createElement('div');
    toxicAura.className = 'gunk-shot-toxic-aura';
    toxicAura.style.left = `${playerX - 60}px`;
    toxicAura.style.top = `${playerY - 60}px`;
    container.appendChild(toxicAura);
    elements.push(toxicAura);

    // Toxic bubbles forming
    for (let i = 0; i < 8; i++) {
      const bubble = document.createElement('div');
      bubble.className = 'gunk-shot-toxic-bubble';
      bubble.style.left = `${playerX + (Math.random() - 0.5) * 60 - 5}px`;
      bubble.style.top = `${playerY + (Math.random() - 0.5) * 40 - 5}px`;
      bubble.style.animationDelay = `${i * 40}ms`;
      container.appendChild(bubble);
      elements.push(bubble);
    }

    // PHASE 2: Sludge Launch (400-900ms)
    const phase2Timer = setTimeout(() => {
      // Main sludge blob projectile
      const sludge = document.createElement('div');
      sludge.className = 'gunk-shot-projectile';
      sludge.style.left = `${playerX}px`;
      sludge.style.top = `${playerY}px`;
      sludge.style.setProperty('--dx', `${dx}px`);
      sludge.style.setProperty('--dy', `${dy}px`);
      container.appendChild(sludge);
      elements.push(sludge);

      // Sludge drip trail
      for (let i = 0; i < 16; i++) {
        const dripTimer = setTimeout(() => {
          const drip = document.createElement('div');
          drip.className = 'gunk-shot-drip';
          const progress = i / 16;
          drip.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 25 - 5}px`;
          drip.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 25 - 5}px`;
          container.appendChild(drip);
          elements.push(drip);
        }, i * 25);
        elements.push({ timer: dripTimer, type: 'timer' });
      }
    }, 400);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Toxic Splatter Impact (900-1400ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('gunk-shot-opponent-hit');

      // Sludge explosion
      const explosion = document.createElement('div');
      explosion.className = 'gunk-shot-explosion';
      explosion.style.left = `${opponentX - 100}px`;
      explosion.style.top = `${opponentY - 100}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Splatter particles in all directions
      for (let i = 0; i < 20; i++) {
        const splatAngle = (i / 20) * 360;
        const splatDist = 50 + Math.random() * 50;
        const splat = document.createElement('div');
        splat.className = 'gunk-shot-splatter';
        splat.style.left = `${opponentX - 5}px`;
        splat.style.top = `${opponentY - 5}px`;
        splat.style.setProperty('--scatter-x', `${Math.cos(splatAngle * Math.PI / 180) * splatDist}px`);
        splat.style.setProperty('--scatter-y', `${Math.sin(splatAngle * Math.PI / 180) * splatDist}px`);
        splat.style.animationDelay = `${i * 12}ms`;
        container.appendChild(splat);
        elements.push(splat);
      }

      // Toxic shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'gunk-shot-shockwave';
          sw.style.left = `${opponentX - 70}px`;
          sw.style.top = `${opponentY - 70}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }

      // Toxic drip effect at impact
      for (let i = 0; i < 6; i++) {
        const toxDrip = document.createElement('div');
        toxDrip.className = 'gunk-shot-toxic-drip';
        toxDrip.style.left = `${opponentX + (Math.random() - 0.5) * 60 - 4}px`;
        toxDrip.style.top = `${opponentY + (Math.random() - 0.5) * 40 - 4}px`;
        toxDrip.style.animationDelay = `${i * 40}ms`;
        container.appendChild(toxDrip);
        elements.push(toxDrip);
      }
    }, 900);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Poison Aftermath (1400-1700ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('gunk-shot-user-charge');

      // Lingering toxic puddle
      const puddle = document.createElement('div');
      puddle.className = 'gunk-shot-puddle';
      puddle.style.left = `${opponentX - 50}px`;
      puddle.style.top = `${opponentY + 20}px`;
      container.appendChild(puddle);
      elements.push(puddle);
    }, 1400);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1700ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('gunk-shot-user-charge');
      opponentEl.classList.remove('gunk-shot-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1700);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('gunk-shot-user-charge');
      opponentEl.classList.remove('gunk-shot-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

GunkShotAnimation.moveType = 'poison';
